(function () {
  'use strict';

  /**
   * SML Snapshot Reader (was: dosage library).
   *
   * Post 2026-04-21 refactor: the theme does no dosage math. The SML app
   * (producer) pre-computes per-variant duration_days into each product's
   * sml.profile.derived.tables snapshot. This module provides the helpers
   * the theme needs to consume that snapshot:
   *
   *   - tablesForForm               — snapshot reader, returns weight rows
   *     for (animal, form, activeVariation), handling v3.0 Array-Legacy and
   *     v3.1 "_"-slot merges.
   *   - resolveEligibleCards        — structural variant filter per
   *     (animal, form, activeVariation). No math, only shape checks.
   *   - distinctVariationsForForm   — variation keys present for a slice.
   *   - deriveActiveVariation       — single-variation-default rule.
   *
   * Schema validation (validateAnimalDosage, validatePack) and dosage math
   * (resolveUnitsPerDay, getDurationDays, interpolate*, grams-to-units)
   * now live exclusively in the SML app. If a product ships without a
   * snapshot, the advisor simply omits duration/per-day labels.
   *
   * Contract v3.1 §2.4, §6.4, §6.5, §9.1.
   */

  // ---------------------------------------------------------------------------
  // Eligible cards helper
  // ---------------------------------------------------------------------------

  /**
   * Resolve eligible advisor cards for a given (animal, dosageForm, activeVariation).
   * Returns distinct size count and card data.
   *
   * Contract v3.1 §2.4 / §6.1 — one signature, one rule. `activeVariation` must be
   * the result of {@link deriveActiveVariation}. Semantics:
   *  - `activeVariation === null` → product has no variation dimension.
   *    Variants with a `variation` string are excluded (data inconsistency, not
   *    silent filter). Variants without a `variation` pass through.
   *  - `activeVariation === "<key>"` → product has one or more variations and a
   *    default/user-selected one is active. Variants without `variation` always
   *    match (permissive "_"-slot rule for mixed products). Variants with
   *    `variation` match only when the key equals `activeVariation`.
   *
   * The `exclude_from_advisor === true` flag always drops a variant. The
   * fourth argument defaults to `null` for backward-compat with pre-v3.1
   * callers — existing test data without `variation` still behaves correctly.
   *
   * @param {Array} variants - array of { id, dosage_form, variation?, exclude_from_advisor?, dosage: { [animal]: {...} } }
   * @param {string} animal - "dog" | "cat"
   * @param {string} dosageForm - "capsule" | "powder"
   * @param {string|null} [activeVariation=null] - kebab-case variation key, or null
   * @returns {{ distinctSizes: number, cards: Array }}
   */
  function resolveEligibleCards(variants, animal, dosageForm, activeVariation) {
    if (!variants || !Array.isArray(variants)) return { distinctSizes: 0, cards: [] };
    if (activeVariation === undefined) activeVariation = null;

    var cards = [];
    var sizeSet = {};

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v) continue;
      if (v.exclude_from_advisor === true) continue;
      if (v.dosage_form !== dosageForm) continue;
      var ad = v.dosage && v.dosage[animal];
      if (!ad || !ad.active) continue;
      if (!ad.size) continue;

      // Permissive "_"-slot rule: variant without variation matches every
      // activeVariation (including null). Variant with a variation matches only
      // when activeVariation is set and equals the variant's variation.
      if (v.variation) {
        if (activeVariation === null || v.variation !== activeVariation) continue;
      }

      cards.push({
        id: v.id,
        size: ad.size,
        mode: ad.mode
      });
      sizeSet[ad.size] = true;
    }

    return {
      distinctSizes: Object.keys(sizeSet).length,
      cards: cards
    };
  }

  // ---------------------------------------------------------------------------
  // v3.1 variation-slot helpers — contract §6.5 / §9.1
  // ---------------------------------------------------------------------------

  /**
   * Distinct variation keys for a given (animal, form) slice of variants.
   *
   * Animal-aware per R20: a variant only contributes its variation when the
   * given animal is active on that variant. Rationale: a product can have
   * `dog` active on variants with variation and `cat` only on unassigned ones —
   * the cat-side variation row must then stay hidden even though variations
   * exist elsewhere on the product. The app-side helper in `snapshot.ts` is
   * deliberately animal-agnostic (marked APP-ONLY) because the editor uses
   * a union view; the theme must never reuse it as-is.
   *
   * `null` variation values (unassigned / "_"-slot variants) are not counted.
   * Result is stable-sorted (ASCII alphabetical) so callers get a
   * deterministic default via {@link deriveActiveVariation}.
   *
   * @param {Array} variants - flat variant shape from liquid bridge
   * @param {string} animal - "dog" | "cat"
   * @param {string} form - "capsule" | "powder"
   * @returns {Array<string>} distinct variation keys, sorted
   */
  function distinctVariationsForForm(variants, animal, form) {
    if (!variants || !Array.isArray(variants)) return [];
    var seen = {};
    var keys = [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v) continue;
      if (v.exclude_from_advisor === true) continue;
      if (v.dosage_form !== form) continue;
      var ad = v.dosage && v.dosage[animal];
      if (!ad || ad.active !== true) continue;
      if (v.variation && !seen[v.variation]) {
        seen[v.variation] = true;
        keys.push(v.variation);
      }
    }
    return keys.sort();
  }

  /**
   * Derive the active variation from a sorted distinct-variation list and an
   * optional user selection. Contract v3.1 §2.4 Single-Variation-Default-Regel.
   *
   *  - 0 variations → `null` (product has no variation dimension)
   *  - 1 variation  → that single key (row is hidden, but runtime still needs
   *    the key for the `"_"`-merge so that mixed products render their base
   *    variant)
   *  - >1 variation → user selection if it's in the list, else `distinct[0]`
   *
   * @param {Array<string>} distinctVariations - result of {@link distinctVariationsForForm}
   * @param {string|null} [userSelection] - previously clicked variation pill
   * @returns {string|null}
   */
  function deriveActiveVariation(distinctVariations, userSelection) {
    if (!distinctVariations || distinctVariations.length === 0) return null;
    if (userSelection && distinctVariations.indexOf(userSelection) !== -1) return userSelection;
    return distinctVariations[0];
  }

  /**
   * Merge two weight-indexed row arrays. Same weight_kg → variation row wins
   * (first arg). Different weights are kept. Output is ascending by weight.
   */
  function mergeByWeight(variationRows, neutralRows) {
    var merged = [];
    var seenWeights = {};
    var src = [variationRows || [], neutralRows || []];
    for (var s = 0; s < src.length; s++) {
      var list = src[s];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (!row || typeof row.weight_kg !== 'number') continue;
        if (seenWeights[row.weight_kg]) continue;
        seenWeights[row.weight_kg] = true;
        merged.push(row);
      }
    }
    merged.sort(function (a, b) { return a.weight_kg - b.weight_kg; });
    return merged;
  }

  /**
   * Resolve the weight-row list for (animal, form, activeVariation) from a
   * product-like container ({ derived: { tables: ... } }). Handles:
   *  - v3.0 legacy: `tables[animal][form]` is an Array → returned as-is
   *    (virtual "_"-slot)
   *  - v3.1: `tables[animal][form]` is a Record keyed by variation slot.
   *    `activeVariation === null` returns only the `"_"` slot.
   *    `activeVariation === "<key>"` returns the merged list of `<key>` + `"_"`.
   *
   * Contract §6.4, §6.5. The snapshot generator (app-side) writes only
   * non-empty slots — missing slots are treated as empty arrays.
   */
  function tablesForForm(product, animal, form, activeVariation) {
    if (!product || !product.derived || !product.derived.tables) return [];
    var animalTable = product.derived.tables[animal];
    if (!animalTable) return [];
    var formTables = animalTable[form];
    if (!formTables) return [];

    // v3.0 legacy: Array shape — virtual "_"-slot, no variation merge possible
    if (Array.isArray(formTables)) return formTables.slice();

    var neutralRows = formTables['_'] || [];
    if (activeVariation === null || activeVariation === undefined) {
      return neutralRows.slice();
    }
    var variationRows = formTables[activeVariation] || [];
    return mergeByWeight(variationRows, neutralRows);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.SmlDosage = {
    resolveEligibleCards: resolveEligibleCards,
    distinctVariationsForForm: distinctVariationsForForm,
    deriveActiveVariation: deriveActiveVariation,
    tablesForForm: tablesForForm,
    mergeByWeight: mergeByWeight,
  };
})();
